/* eslint-disable operator-linebreak */
/* eslint-disable indent */
import { useRef } from 'react';
import useFetch from 'use-http';
import { useSelector } from 'react-redux';
import mockToken from '../mock/bearerToken';
import getLLEsubdomain from '../helpers/getLLEsubdomain';

export default function useApi() {
  const headers = useRef({});
  const store = useSelector(state => ({
    integrationType: state.technical?.initialize?.integrationType,
    token: state.technical?.initialize?.token,
  }));

  const synchronyRetailerToken = localStorage.getItem('synchronyRetailerToken');

  const isIntegrationType = !!store.integrationType;
  if (isIntegrationType) {
    if (store.token !== '3c007be61d17302fd4e49ff26e2ed471f844dab4') {
      headers.current = {
        Authorization: `Bearer ${synchronyRetailerToken || store.token}`,
      };
    }
  } else {
    headers.current = {
      Authorization: `Bearer ${synchronyRetailerToken || store.token}`,
    };
  }

  const {
    post,
    patch,
    loading,
    error,
    get,
    response,
    request: httpRequest,
  } = useFetch(isIntegrationType ? '/storis-api' : '', {
    cachePolicy: 'no-cache',
    retries: 3,
    // eslint-disable-next-line no-shadow
    retryOn: async ({ error, response }) => {
      // A 409 specifically reporting an existing KP+ membership (from /api/v3/kplus/enroll/)
      // is an expected, not a transient, outcome - LeaseAgreement.js's own enrollment logic
      // already treats this exact response as success, so retrying it would just delay that
      // outcome with a few wasted, slow no-op attempts.
      if (response?.status === 409) {
        const body = await response
          .clone()
          .json()
          .catch(() => null);
        if (body?.error === 'A live KP+ membership already exists for this lease') {
          return false;
        }
      }
      // returns true or false to determine whether to retry
      return error || (response && response.status >= 300);
    },
    retryDelay: ({ attempt }) => {
      return Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000);
    },
    interceptors: {
      request: async ({ options }) => {
        options.headers = { ...options.headers, ...headers.current };
        return options;
      },
      // eslint-disable-next-line no-shadow
      response: async ({ response }) => {
        return response;
      },
    },
    headers,
  });

  // Dedicated instance for /sync/: polls on a fixed 1.5s interval until success,
  // instead of the shared instance's capped exponential backoff above.
  const {
    post: syncPost,
    loading: syncLoading,
    error: syncError,
    response: syncResponse,
  } = useFetch(isIntegrationType ? '/storis-api' : '', {
    cachePolicy: 'no-cache',
    retries: 0,
    interceptors: {
      request: async ({ options }) => {
        options.headers = { ...options.headers, ...headers.current };
        return options;
      },
    },
    headers,
  });

  // Request Verification Code
  // https://docs.katapult.com/reference#post_user-auth-retailer
  const loginRequest = async payload => {
    return post('/api/v3/user/auth/retailer/', payload);
  };

  // Send Verification Code To Email
  // https://docs.katapult.com/reference#post_user-auth-retailer
  const sendCodeToEmailRequest = async payload => {
    return post('/api/ng/user/auth/email_code/', payload);
  };

  // Submit Verification Code
  // https://docs.katapult.com/reference#post_user-auth-verify
  const confirmationLoginRequest = async payload => {
    return post('/api/v3/user/auth/verify/', payload);
  };

  // Get Pay Frequency information
  // ToDo: documentation link should be provided
  const payFrequencyLookup = async payload => {
    return post('/api/v3/application/payfrequency_lookup/', { ...payload });
  };
  // {"available_limit": "382.88", "first_name": "Kents", "payfrequency_exists": true}

  // Build the Application
  // https://docs.katapult.com/reference#patch_application-uid-
  const patchApplicationRequest = async (uid, payload) => {
    return patch(`/api/v3/application/${uid}/`, { ...payload });
  };

  // Contract - Lease Agreement
  // https://docs.katapult.com/reference#get_-uid-contract
  const getLeaseAgreementContract = async uid => {
    return get(`/api/v3/application/${uid}/contract/`);
  };

  // Contract - Lease Agreement
  // https://docs.katapult.com/reference#get_-uid-contract
  const getLeaseAgreementContractPdf = async uid => {
    return get(`/api/v3/application/${uid}/contract/?format=pdf`);
  };

  // Contract - Price Tag Disclosure
  // https://docs.katapult.com/reference#get_-uid-contract
  const getPriceTagDisclosureContract = async uid => {
    return get(`/api/v3/application/${uid}/contract/?type=pricing`);
  };

  // Contract - Price Tag Disclosure
  // https://docs.katapult.com/reference#get_-uid-contract
  const getWarrantyContract = async uid => {
    return get(`/api/v3/application/${uid}/contract/?type=warranty`);
  };

  const getAonContract = async uid => {
    return get(`/api/v3/application/${uid}/contract/?type=aon_agreement`);
  };

  // Contract - Katapult Protection+ (KP+) Agreement
  // Real response shape is { application_id, html }, unlike every other contract
  // endpoint in this file ({ contract }) - normalize it here so callers (and
  // useAgreementContract) can treat all contract-fetching methods identically.
  const getKplusAgreement = async applicationId => {
    const kplusResponse = await get(`/api/v3/kplus/agreement/?application_id=${applicationId}`);
    return kplusResponse && { contract: kplusResponse.html };
  };

  // Contract - Katapult Protection+ (KP+) Acknowledgment
  // Same response-shape normalization as getKplusAgreement above.
  const getKplusAcknowledgement = async applicationId => {
    const kplusResponse = await get(`/api/v3/kplus/agreement/?application_id=${applicationId}&type=acknowledgement`);
    return kplusResponse && { contract: kplusResponse.html };
  };

  // Enroll (or decline) Katapult Protection+ (KP+) once the Agreement has been reviewed.
  // Mirrors the existing AngularJS app's kplus/enroll endpoint.
  const enrollKplus = async (applicationId, agreementAccepted) => {
    return post('/api/v3/kplus/enroll/', {
      application_id: applicationId,
      agreement_accepted: agreementAccepted,
    });
  };

  // Get the current Katapult Protection+ (KP+) membership/enrollment status.
  // Mirrors the existing AngularJS app's kplus/status endpoint.
  const getKplusMembershipStatus = async applicationId => {
    return get(`/api/v3/kplus/status/?application_id=${applicationId}`);
  };

  // Cancel an existing Katapult Protection+ (KP+) membership.
  // Mirrors the existing AngularJS app's kplus/cancel endpoint.
  const cancelKplusMembership = async applicationUid => {
    return post('/api/v3/kplus/cancel/', { application_id: applicationUid });
  };

  // Unenroll from Katapult Protection+ (KP+) when the customer declines/removes coverage
  // on the Checkout page, before the membership is otherwise confirmed/cancelled.
  const unenrollKplus = async applicationUid => {
    const body = await post('/api/v3/kplus/unenroll/', { application_id: applicationUid });
    if (httpRequest.error || !response.ok || body?.error) {
      throw new Error(body?.error || 'Unable to unenroll from Katapult Protection+');
    }
    return body;
  };

  const getAonEnrollmentStatus = async userid => {
    const url = `https://${getLLEsubdomain('dot')}katapult.com/api/v3/aon/${userid}/enrollment-status/`;
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${synchronyRetailerToken || mockToken}`,
      },
    });
    return res.json();
  };

  const updateAonEnrollmentStatus = async (userid, payload) => {
    const url = `https://${getLLEsubdomain('dot')}katapult.com/api/v3/aon/${userid}/enrollment-status/`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${synchronyRetailerToken || mockToken}`,
      },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    // This is a raw fetch() to a different host, bypassing use-http's post()/response state -
    // callers otherwise have no way to detect a failed request the way enrollKplus's shared
    // `response` object lets other enrollment flows do.
    return { ...body, ok: res.ok, status: res.status };
  };

  // Resolve an in-store location (store_id) into the in-store JWT, retailer public token and logo.
  // No auth: a raw fetch so the shared instance's Authorization header is not sent.
  const instoreLocationRequest = async storeId => {
    const res = await fetch('/api/v3/instore/location/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId }),
    });
    const body = await res.json().catch(() => null);
    return { ...body, ok: res.ok, status: res.status };
  };

  const postWithToken = async (url, token, body, contentType = 'application/json') => {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': contentType,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`POST ${url} failed with status ${res.status}`);
    }
    return res;
  };

  const vccSyncRequest = async (uid, instoreToken) => {
    const res = await postWithToken(
      `https://${getLLEsubdomain('dot')}katapult.com/api/v3/application/vcc-sync/`,
      instoreToken,
      { uid },
    );
    const { token } = await res.json();
    if (!token) {
      throw new Error('vcc-sync responded without a token');
    }
    return token;
  };

  const viewCardRequest = async jwt => {
    const res = await postWithToken(`https://api.${getLLEsubdomain('dot')}katapult.com/v1/viewcard`, jwt, {});
    const html = await res.text();
    if (!html) {
      throw new Error('viewcard responded without a card');
    }
    return html;
  };

  // Initialize the Lease for Checkout
  // https://docs.katapult.com/reference#post_application-initialize
  const checkoutRequest = async payload => {
    return post('/api/v3/application/checkout/', payload);
  };

  // Contract
  // https://docs.katapult.com/reference#get_-uid-contract
  const contractRequest = async (uid, payload) => {
    return post('/api/v3/contract/', payload);
  };

  const SYNC_RETRY_DELAY_MS = 1500;
  const SYNC_MAX_ATTEMPTS = 40; // ~1 minute of polling before giving up

  const syncRequest = async uid => {
    let data;
    for (let attempt = 1; attempt <= SYNC_MAX_ATTEMPTS; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      data = await syncPost('/api/v3/application/sync/', { uid });
      if (syncResponse.ok) {
        return data;
      }
      if (attempt < SYNC_MAX_ATTEMPTS) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, SYNC_RETRY_DELAY_MS));
      }
    }
    return data;
  };

  return {
    request: {
      payFrequencyLookup,
      loginRequest,
      confirmationLoginRequest,
      patchApplicationRequest,
      checkoutRequest,
      contractRequest,
      getPriceTagDisclosureContract,
      getLeaseAgreementContract,
      getLeaseAgreementContractPdf,
      getWarrantyContract,
      getAonContract,
      instoreLocationRequest,
      vccSyncRequest,
      viewCardRequest,
      getKplusAgreement,
      getKplusAcknowledgement,
      enrollKplus,
      getKplusMembershipStatus,
      cancelKplusMembership,
      unenrollKplus,
      getAonEnrollmentStatus,
      updateAonEnrollmentStatus,
      sendCodeToEmailRequest,
      syncRequest,
    },

    loading,
    response,
    error,
    syncLoading,
    syncResponse,
    syncError,
  };
}
